import { SupportingActorType } from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import { FormHelperText, IconButton } from '@material-ui/core';
import { PlusCircle, Trash2 } from 'lucide-react';
import React from 'react';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../../layout/Flex';
import { useUserRuleConfigWithAutocompletion } from '../../../../../../../services/user';
import {
  EMPTY_RULE_GROUP,
  hasIncompleteCondition,
  isEmptyRuleGroup,
  RuleBuilder
} from '../../../../../components/RuleBuilder';
import { NewUxButton } from '../../../../../components/Builders/newux/kit/Button';
import { HalfWidthField } from '../../ActorDialogParts';
import * as UX from '../../../../../components/Builders/newux/kit';
import type {
  ActorTypeFieldsProps,
  ActorTypeForm,
  StaticUserActorState
} from '..';
import { AutocompleteUserId } from '../AutocompleteUserId';
import { staticUserFromActor, staticUserToActor } from './samePerson';

// The type picker's "Based on rules" choice: a static user with rule-based
// options, not an actor type of its own.
export const RULE_BASED_USER_OPTION = 'ruleBasedUser';

const RuleBuilderContainer = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
`;

const RuleBasedActorFields = ({
  state,
  onChange,
  disabled,
  showValidation,
  orgId,
  customFields,
  tpl
}: ActorTypeFieldsProps<StaticUserActorState>) => {
  const { t } = useUnsafeTranslation();
  const ruleConfig = useUserRuleConfigWithAutocompletion(
    orgId,
    customFields,
    tpl.data.tz,
    {
      orgId,
      experienceTemplateId: tpl.id
    }
  );
  const setOption = (
    i: number,
    next: StaticUserActorState['ruleBasedUserIds'][number]
  ) => {
    const nextRules = [...state.ruleBasedUserIds];
    nextRules[i] = next;
    onChange({ ...state, ruleBasedUserIds: nextRules });
  };
  return (
    <>
      {state.ruleBasedUserIds.map((r, i) => (
        <FlexContainerVertical fullWidth spacing={2} key={i}>
          <FlexContainer fullWidth>
            <UX.FieldLabel style={{ marginBottom: 0 }}>
              {t('Option #{{ count }}', { count: i + 1 })}
            </UX.FieldLabel>
            {i !== 0 && (
              <IconButton
                size="small"
                disabled={disabled}
                aria-label={t('Remove option')}
                onClick={() => {
                  const nextRules = [...state.ruleBasedUserIds];
                  nextRules.splice(i, 1);
                  onChange({ ...state, ruleBasedUserIds: nextRules });
                }}
              >
                <Trash2 size={14} />
              </IconButton>
            )}
          </FlexContainer>
          <FlexContainerVertical fullWidth spacing={0}>
            <AutocompleteUserId
              label=""
              placeholder={t('Select user')}
              orgId={orgId}
              value={r.userId}
              disabled={disabled}
              error={showValidation && !r.userId}
              helperText={
                showValidation && !r.userId
                  ? t('Please select a user')
                  : undefined
              }
              onChange={(nextUserId) =>
                setOption(i, { ...r, userId: nextUserId })
              }
            />
            <RuleBuilderContainer>
              <RuleBuilder
                value={r.rule}
                onChange={(nextRule) => {
                  if (nextRule) {
                    setOption(i, { ...r, rule: nextRule });
                  }
                }}
                config={ruleConfig}
                variant="normal"
                nullable
                showEmptyRowWhenNull={false}
                newUx
              />
              {!showValidation ? null : isEmptyRuleGroup(r.rule) ? (
                <FormHelperText error>
                  {t('Please provide at least one rule')}
                </FormHelperText>
              ) : hasIncompleteCondition(r.rule) ? (
                <FormHelperText error>
                  {t(
                    "Finish or remove the filter row that's missing a value before saving."
                  )}
                </FormHelperText>
              ) : null}
            </RuleBuilderContainer>
          </FlexContainerVertical>
        </FlexContainerVertical>
      ))}

      <FlexContainer fullWidth>
        <NewUxButton
          variant="text"
          color="primary"
          disabled={disabled}
          startIcon={<PlusCircle size={16} />}
          // Offsets the button's own left padding so the icon lines up with
          // the fields above.
          style={{ marginLeft: -14 }}
          onClick={() =>
            onChange({
              ...state,
              ruleBasedUserIds: [
                ...state.ruleBasedUserIds,
                { rule: EMPTY_RULE_GROUP, userId: '' }
              ]
            })
          }
        >
          {t('Add another option')}
        </NewUxButton>
      </FlexContainer>

      <FlexContainerVertical fullWidth spacing={2}>
        <UX.FieldLabel style={{ marginBottom: 0 }}>
          {t('Default when no other option matches')}
        </UX.FieldLabel>
        <FlexContainer fullWidth>
          <UX.RingIconButton
            type="button"
            selected={!state.defaultUnassigned}
            disabled={disabled}
            onClick={() => onChange({ ...state, defaultUnassigned: false })}
          >
            {t('A specific person')}
          </UX.RingIconButton>
          <UX.RingIconButton
            type="button"
            selected={state.defaultUnassigned}
            disabled={disabled}
            onClick={() =>
              onChange({ ...state, defaultUnassigned: true, defaultUserId: '' })
            }
          >
            {t('Leave unassigned')}
          </UX.RingIconButton>
        </FlexContainer>
        {!state.defaultUnassigned && (
          <HalfWidthField>
            <AutocompleteUserId
              label=""
              placeholder={t('Select user')}
              orgId={orgId}
              value={state.defaultUserId}
              disabled={disabled}
              error={showValidation && !state.defaultUserId}
              helperText={
                showValidation && !state.defaultUserId
                  ? t('Please select a user')
                  : undefined
              }
              onChange={(nextUserId) =>
                onChange({ ...state, defaultUserId: nextUserId })
              }
            />
          </HalfWidthField>
        )}
      </FlexContainerVertical>
    </>
  );
};

/**
 * "Based on rules": a static user whose person depends on rules about the
 * participant, with a default when none matches.
 */
export const ruleBasedActorType: ActorTypeForm<StaticUserActorState> = {
  option: RULE_BASED_USER_OPTION,
  label: (t) => t(`Based on rules`),
  matches: (actor) =>
    actor.type === SupportingActorType.STATIC_USER &&
    !!actor.ruleBasedUserIds.length,
  fromActor: staticUserFromActor,
  create: (base) => ({
    name: base.name,
    type: SupportingActorType.STATIC_USER,
    defaultUserId: '',
    ruleBasedUserIds: [{ rule: EMPTY_RULE_GROUP, userId: '' }],
    defaultUnassigned: false,
    ignoreWhenMissing: base.ignoreWhenMissing,
    color: base.color
  }),
  // Every option needs a person and a usable rule; the default needs a person
  // unless it's explicitly left unassigned.
  isComplete: (state) =>
    state.ruleBasedUserIds.every(
      (o) =>
        !!o.userId &&
        !isEmptyRuleGroup(o.rule) &&
        !hasIncompleteCondition(o.rule)
    ) &&
    (state.defaultUnassigned || !!state.defaultUserId),
  toActor: staticUserToActor,
  Fields: RuleBasedActorFields
};
